// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Skeleton,
  useTheme,
  useMediaQuery,
  Stack,
  alpha,
  TablePagination
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { feeAPI, accountingAPI, teacherAPI } from '../services/api';
import { formatCurrency, parseNumber, calculatePercentage } from '../utils/formatters';
import { DashboardStatCards, DashboardFinancialSummary } from './DashboardCards';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [dashboardData, setDashboardData] = useState({
    totalStudents: 0,
    totalCollected: 0,
    totalDue: 0,
    overdueCount: 0,
    recentPayments: [],
    monthlyCollection: [],
    totalExpenses: 0,
    cashAtHand: 0,
    cashInBank: 0,
    cashOnMobileMoney: 0,
    accounts: [],
    totalTeachers: 0
  });
  const [recentPayments, setRecentPayments] = useState([]);
  const [recentPaymentsPagination, setRecentPaymentsPagination] = useState({ page: 0, totalRecords: 0, pageSize: 5 });
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({ page: 0, totalRecords: 0, pageSize: 5 });
  const [accounts, setAccounts] = useState([]);
  const [accountsPagination, setAccountsPagination] = useState({ page: 0, totalRecords: 0, pageSize: 10 });
  const [loading, setLoading] = useState(true);

  // ── Chart data ──────────────────────────────────────────────────────────────
  const monthlyCollectionData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Collections (UGX)',
      data: dashboardData.monthlyCollection || [0,0,0,0,0,0,0,0,0,0,0,0],
      backgroundColor: '#4B49AC',
      borderColor: '#4B49AC',
      borderWidth: 2,
      fill: false,
    }],
  };

  const feeStatusData = {
    labels: ['Paid', 'Pending', 'Overdue'],
    datasets: [{
      data: [
        parseFloat(dashboardData.totalCollected || 0),
        parseFloat(dashboardData.totalDue || 0),
        parseFloat(dashboardData.overdueAmount || 0)
      ],
      backgroundColor: ['#4caf50', '#ff9800', '#f44336'],
      borderWidth: 0,
    }],
  };

  const expenseBreakdownData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Expenses (UGX)',
      data: dashboardData.monthlyExpenses || [0,0,0,0,0,0,0,0,0,0,0,0],
      backgroundColor: '#f44336',
      borderColor: '#f44336',
      borderWidth: 2,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => 'UGX ' + v.toLocaleString() }
      }
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
  };

  // ── Data fetching ───────────────────────────────────────────────────────────
  useEffect(() => {
    fetchDashboardData();
    fetchTransactions(0);
    fetchRecentPayments(0);
    fetchAccounts(0);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashboardResponse, accountsResponse, teachersResponse] = await Promise.allSettled([
        feeAPI.getDashboard(),
        accountingAPI.getAccounts(),
        teacherAPI.getActive()
      ]);

      let data = {};
      let accounts = [];
      let totalTeachers = 0;

      if (dashboardResponse.status === 'fulfilled') data = dashboardResponse.value.data;
      if (accountsResponse.status === 'fulfilled') accounts = accountsResponse.value.data;
      if (teachersResponse.status === 'fulfilled') totalTeachers = teachersResponse.value.data.length;

      const cashAtHand = accounts
        .filter(a => a.account_type === 'cash')
        .reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
      const cashInBank = accounts
        .filter(a => a.account_type === 'bank')
        .reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
      const cashOnMobileMoney = accounts
        .filter(a => a.account_type === 'mobile_money')
        .reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);

      setDashboardData({ ...data, accounts, cashAtHand, cashInBank, cashOnMobileMoney, totalExpenses: data.totalExpenses || 0, totalTeachers });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (pageNum = 0, perPage = 5) => {
    try {
      const { data } = await feeAPI.getPayments({ page: pageNum + 1, per_page: perPage });
      setTransactions(data.data || []);
      setTransactionsPagination({ page: pageNum, totalRecords: data.total || 0, pageSize: perPage });
    } catch { setTransactions([]); }
  };

  const fetchRecentPayments = async (pageNum = 0, perPage = 5) => {
    try {
      const { data } = await feeAPI.getPayments({ page: pageNum + 1, per_page: perPage });
      setRecentPayments(data.data || []);
      setRecentPaymentsPagination({ page: pageNum, totalRecords: data.total || 0, pageSize: perPage });
    } catch { setRecentPayments([]); }
  };

  const fetchAccounts = async (pageNum = 0, perPage = 10) => {
    try {
      const { data } = await accountingAPI.getAccounts({ page: pageNum + 1, per_page: perPage });
      setAccounts(Array.isArray(data) ? data : data.data || []);
      setAccountsPagination({ page: pageNum, totalRecords: data.total || (Array.isArray(data) ? data.length : 0), pageSize: perPage });
    } catch { setAccounts([]); }
  };

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width={200} height={20} sx={{ mb: 4 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2, flex: '1 1 200px' }} />
          ))}
        </Box>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 3 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          {[...Array(4)].map((_, i) => (
            <Card key={i} sx={{ flex: '1 1 400px' }}>
              <CardContent>
                <Skeleton variant="text" width={250} height={24} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={280} />
              </CardContent>
            </Card>
          ))}
        </Box>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Card>
          <CardContent>
            <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
            {[...Array(6)].map((_, i) => (
              <Box key={i} display="flex" gap={2} py={1}>
                {[80, 120, 100, 60, 80, 150].map((w, j) => (
                  <Skeleton key={j} variant="text" width={w} height={16} />
                ))}
              </Box>
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Welcome to School Sync!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          All systems are running smoothly! You have{' '}
          <Typography component="span" color="error" fontWeight="bold">
            {dashboardData.overdueCount} overdue fees
          </Typography>
        </Typography>
      </Box>

      {/* ── Redesigned stat cards ── */}
      <DashboardStatCards dashboardData={dashboardData} />

      {/* ── Redesigned financial summary ── */}
      <DashboardFinancialSummary dashboardData={dashboardData} />

      {/* ── Charts row ── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: { xs: 2, md: 4 } }}>
        <Card sx={{ flex: '1 1 400px' }}>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Monthly Fee Collection Trend
            </Typography>
            <Box height={{ xs: 200, sm: 250, md: 300 }}>
              <Line data={monthlyCollectionData} options={chartOptions} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 400px' }}>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Fee Status Distribution
            </Typography>
            <Box height={{ xs: 200, sm: 250, md: 300 }}>
              <Doughnut data={feeStatusData} options={doughnutOptions} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 400px' }}>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Monthly Expense Breakdown
            </Typography>
            <Box height={{ xs: 180, sm: 220, md: 250 }}>
              <Bar data={expenseBreakdownData} options={chartOptions} />
            </Box>
          </CardContent>
        </Card>

        {/* Recent Payments card */}
        <Card sx={{ flex: '1 1 400px' }}>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Recent Payments
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 300 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentPayments.length > 0 ? (
                    recentPayments.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {p.student?.last_name} {p.student?.first_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(p.payment_date).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`UGX ${parseFloat(p.amount || 0).toLocaleString()}`} color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">No recent payments</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={recentPaymentsPagination.totalRecords}
              page={recentPaymentsPagination.page}
              onPageChange={(_, p) => fetchRecentPayments(p, recentPaymentsPagination.pageSize)}
              rowsPerPage={recentPaymentsPagination.pageSize}
              onRowsPerPageChange={(e) => fetchRecentPayments(0, parseInt(e.target.value, 10))}
              rowsPerPageOptions={[5, 10, 15]}
              labelRowsPerPage="Rows"
              size="small"
            />

            {/* Monthly summary mini-section */}
            <Box mt={2} pt={2} borderTop={`1px solid ${alpha('#000', 0.08)}`}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Monthly Summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {[
                  { label: 'Income', value: parseFloat(dashboardData.totalIncome || dashboardData.totalCollected || 0), color: 'success.main', icon: TrendingUp, iconColor: 'success' },
                  { label: 'Expenses', value: parseFloat(dashboardData.totalExpenses || 0), color: 'error.main', icon: TrendingDown, iconColor: 'error' },
                  { label: 'Net', value: parseFloat(dashboardData.netIncome || 0), color: parseFloat(dashboardData.netIncome || 0) >= 0 ? 'success.main' : 'error.main', icon: TrendingUp, iconColor: parseFloat(dashboardData.netIncome || 0) >= 0 ? 'success' : 'error' },
                ].map(({ label, value, color, icon: Icon, iconColor }) => (
                  <Box key={label} sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
                      <Icon color={iconColor} sx={{ fontSize: 16 }} />
                      <Typography variant="body2" color={color} fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {value.toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ── Account Balances Table ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Account Balances</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">Current Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.length > 0 ? (
                  accounts.map((acc) => (
                    <TableRow key={acc.id} hover>
                      <TableCell>{acc.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={acc.account_type}
                          color={acc.account_type === 'cash' ? 'success' : acc.account_type === 'bank' ? 'primary' : 'info'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{acc.provider || '-'}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={parseFloat(acc.current_balance) > 0 ? 'success.main' : 'text.secondary'}
                        >
                          UGX {parseFloat(acc.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No accounts found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={accountsPagination.totalRecords}
            page={accountsPagination.page}
            onPageChange={(_, p) => fetchAccounts(p, accountsPagination.pageSize)}
            rowsPerPage={accountsPagination.pageSize}
            onRowsPerPageChange={(e) => fetchAccounts(0, parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 15, 20]}
            labelRowsPerPage="Rows per page"
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>

      {/* ── Recent Transactions Table ── */}
      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Recent Transactions
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', width: '100%' }}>
            <Table stickyHeader sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 100 }}>Date</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Student</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Fee Group</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Payment Method</TableCell>
                  <TableCell sx={{ minWidth: 120 }} align="right">Amount</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Account</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length > 0 ? (
                  transactions.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{new Date(p.payment_date).toLocaleDateString()}</TableCell>
                      <TableCell>{p.student?.last_name} {p.student?.first_name}</TableCell>
                      <TableCell>{p.fee_group?.name}</TableCell>
                      <TableCell>
                        <Chip label={p.payment_method?.replace('_', ' ').toUpperCase()} color="primary" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          UGX {parseFloat(p.amount).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{p.account?.name || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No recent transactions found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={transactionsPagination.totalRecords}
            page={transactionsPagination.page}
            onPageChange={(_, p) => fetchTransactions(p, transactionsPagination.pageSize)}
            rowsPerPage={transactionsPagination.pageSize}
            onRowsPerPageChange={(e) => fetchTransactions(0, parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 15, 20]}
            labelRowsPerPage="Rows per page"
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
